import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateOrganization,
  deactivateOrganization,
  getDeactivationStatus,
  isTerminalDeactivationStatus,
  type DeactivateAcceptedResponse,
  type DeactivationStatusResponse,
} from '../../api/organizationStatus';

const DEACTIVATION_POLL_INTERVAL_MS = 3_000;

const invalidateOrgLists = (queryClient: ReturnType<typeof useQueryClient>, orgId: string) => {
  queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  queryClient.invalidateQueries({ queryKey: ['organizations'] });
};

/** Activate an org — sync. */
export const useActivateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => activateOrganization(orgId),
    onSuccess: (_data, orgId) => invalidateOrgLists(queryClient, orgId),
  });
};

/**
 * Deactivate an org — returns 202 with a `request_id`. Caller is responsible
 * for polling via `useDeactivationStatus(request_id)` until the status is
 * terminal, then refetching org data.
 */
export const useDeactivateOrganization = () => {
  return useMutation<DeactivateAcceptedResponse, Error, string>({
    mutationFn: (orgId: string) => deactivateOrganization(orgId),
  });
};

/**
 * Poll the deactivation status row every 3s until the orchestrator reaches a
 * terminal state. Pass `null`/`undefined` to disable polling (e.g. when no
 * deactivation is in flight).
 */
export const useDeactivationStatus = (
  requestId: string | null | undefined,
  options?: {
    onTerminal?: (data: DeactivationStatusResponse) => void;
  }
) => {
  return useQuery<DeactivationStatusResponse>({
    queryKey: ['deactivationStatus', requestId],
    queryFn: async () => {
      const data = await getDeactivationStatus(requestId as string);
      if (isTerminalDeactivationStatus(data.status)) {
        options?.onTerminal?.(data);
      }
      return data;
    },
    enabled: !!requestId,
    refetchInterval: (query) => {
      const data = query.state.data as DeactivationStatusResponse | undefined;
      if (data && isTerminalDeactivationStatus(data.status)) return false;
      return DEACTIVATION_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: true,
  });
};
